<!-- Component for displaying list view inside an overlay -->
<template>
	<div class="flex flex-1 flex-col">
		<!--	Product Filter and Sort By Dropdowns	-->
		<div class="sticky top-0 w-full bg-white p-1 pb-2 pl-5">
			<div class="relative flex w-1/4">
				<TagsDropdown
					:product-tags="productTags"
					@select="handleTagOption"
				/>
				<SortByDropdown @sort-by="handleSortByOption" />
			</div>
		</div>
		<!-- Accordion with sections -->
		<BaseAccordion
			v-model="selectedItem"
			:sections="displaySections"
			:productPlans="productPlans"
		/>
	</div>

	<div class="w-2/5 overflow-y-auto! pr-4">
		<SidePanelPackage
			v-if="selectedPackage"
			:pkg="selectedPackage"
		/>

		<FormProduct
			v-else-if="selectedProduct"
			:product="selectedProduct"
			:key="selectedProduct?.id"
			hasCounter
		/>

		<FormProduct
			v-else-if="selectedOap && mockProductOap"
			:product="mockProductOap"
		/>

		<FormProduct
			v-else-if="selectedEverplans && mockProductEverplans"
			:product="mockProductEverplans"
		/>

		<div v-else>
			{{
				selectedItem?.category
					? selectedItem.category.charAt(0).toUpperCase() +
						selectedItem.category.slice(1) +
						' component'
					: 'Component'
			}}
			{{ t('general.comingSoon') }}
		</div>
	</div>
</template>

<script setup lang="ts">
import BaseAccordion, {
	type AccordionSection,
} from '../common/BaseAccordion.vue'
import FormProduct from '../merchandise/FormProduct.vue'
import type { PbLineItem, PbLineItemPackage, SfTag } from '@/apiTypes'
import type { IndexedPlan } from '@/classes/Plan'
import type { PbLineItemProductAndPricingOptions } from '@/classes/PriceBook'
import SidePanelPackage from '@/components/packages/SidePanelPackage.vue'
import SortByDropdown from '@/components/presentation/SortByDropdown.vue'
import TagsDropdown from '@/components/presentation/TagsDropdown.vue'
import {
	buildProductTagOptions,
	matchesSelectedTag,
	type SortByOption,
	sortProductListItems,
} from '@/components/presentation/productListControls'
import { PbProductType } from '@/enums/PbProductType'
import { PbSubcategory } from '@/enums/PbSubcategory'
import { usePresentationLocale } from '@/i18n'
import { useMerchandise } from '@/mixins/merchandise'
import { useAuthStore } from '@/stores/auth'
import { useSessionStore } from '@/stores/session'
import { computed, ref, watch } from 'vue'

const authStore = useAuthStore()

const { t } = usePresentationLocale()
const {
	priceBook,
	isLineItemInPlan,
	mockProductOap,
	mockProductEverplans,
	calculatePackageTotal,
} = useMerchandise()

const sessionStore = useSessionStore()
const plans = computed(() => sessionStore.activePlans ?? [])
const selectedItem = ref<PbLineItem | undefined>({} as PbLineItem)
const selectedTag = ref<SfTag | null>(null)

/**
 * Only expose tags belonging to products displayed in this overlay. Packages
 * remain visible when filtering because PriceBook normalizes tags for products,
 * while package topic assignments are still in their API shape.
 */
const productTags = computed<SfTag[]>(() => {
	const displayedProductIds = new Set(
		sections.value
			.flatMap((section) => section.items)
			.filter(
				(item): item is PbLineItemProductAndPricingOptions =>
					item.recordType === 'Product'
			)
			.map((item) => item.id)
	)

	return buildProductTagOptions(
		priceBook.value?.getProductTags((product) =>
			displayedProductIds.has(product.id)
		) ?? [],
		t('general.all')
	)
})

const selectedSortBy = ref<SortByOption['value']>('alphabetical')

const recommendedItems = computed(() => {
	const items: Array<PbLineItemProductAndPricingOptions | PbLineItemPackage> =
		[]

	if (mockProductOap.value) items.push(mockProductOap.value)
	if (authStore.isEverplansAuthorized && mockProductEverplans.value) {
		items.push(mockProductEverplans.value)
	}

	return items
})

const caskets = computed(
	() =>
		priceBook.value?.products.filter(
			(p) =>
				p.subcategory === PbSubcategory.MerchandiseCasket ||
				p.subcategory === PbSubcategory.MerchandiseOuterBurialContainer
		) ?? []
)
const urns = computed(
	() =>
		priceBook.value?.products.filter(
			(p) => p.subcategory === PbSubcategory.MerchandiseUrn
		) ?? []
)

const basicServices = computed(
	() =>
		priceBook.value?.products.filter((p) =>
			[
				PbProductType.Disposition,
				PbProductType.Service,
				PbProductType.Transportation,
			].includes(p.category)
		) ?? []
)

/** Split packages into Essentials and non-Essentials, sorted by price */
const essentialsPackages = computed(
	() =>
		priceBook.value?.packages?.filter(
			(p) => p.subcategory === PbSubcategory.PackageEssentials
		) ?? []
)
const otherPackages = computed(
	() =>
		priceBook.value?.packages
			?.filter((p) => p.subcategory !== PbSubcategory.PackageEssentials)
			.map((pkg) => {
				return {
					...pkg,
					fixedPrice: calculatePackageTotal(pkg.id),
				}
			}) ?? []
)

/**
 * Map products and packages to their corresponding plans.
 */
/**
 * Map products and packages to their corresponding plans.
 * Special-case handling for OAP and Everplans so they don't break isLineItemInPlan.
 */
const productPlans = computed<Record<string, IndexedPlan[]>>(() => {
	if (!plans.value.length) return {}
	if (!priceBook.value?.products && !priceBook.value?.packages) return {}

	// Collect all items: real products, real packages, plus mock OAP/Everplans
	const items: PbLineItem[] = [
		...(priceBook.value.products ?? []),
		...(priceBook.value.packages ?? []),
	]

	if (mockProductOap.value) items.push(mockProductOap.value)
	if (mockProductEverplans.value) items.push(mockProductEverplans.value)

	return items.reduce<Record<string, IndexedPlan[]>>((acc, item) => {
		acc[item.id] = plans.value
			.map((plan, index): IndexedPlan => ({ index, plan }))
			.filter((indexedPlan) => {
				// Special handling based on category, not hard-coded IDs
				if (item.category === PbProductType.Oap) {
					return indexedPlan.plan.oapIncluded
				}
				if (item.category === PbProductType.Everplans) {
					return indexedPlan.plan.everplansIncluded ?? false
				}
				// Fallback for normal line items
				return isLineItemInPlan(item, indexedPlan.plan)
			})
		return acc
	}, {})
})

// Find the corresponding package based on the selected item's ID
const selectedPackage = computed<PbLineItemPackage | undefined>(() => {
	return priceBook.value?.packages.find((p) => p.id === selectedItem.value?.id)
})

// Find the corresponding product based on the selected item's ID
const selectedProduct = computed<
	PbLineItemProductAndPricingOptions | undefined
>(() => {
	return priceBook.value?.products.find((p) => p.id === selectedItem.value?.id)
})

/** True if the selected item is OAP */
const selectedOap = computed(
	() => selectedItem.value?.category === PbProductType.Oap
)

const selectedEverplans = computed(
	() => selectedItem.value?.category === PbProductType.Everplans
)

const sortItems = (
	items: Array<PbLineItemProductAndPricingOptions | PbLineItemPackage>
) => {
	return sortProductListItems(items, selectedSortBy.value)
}

/** Accordion sections with conditional Essentials Packages */
const sections = computed<AccordionSection[]>(() => {
	const baseSections: AccordionSection[] = [
		{
			key: PbProductType.Service,
			title: t('presentation.services'),
			items: basicServices.value,
		},
		{
			key: 'recommendedItems',
			title: t('presentation.recommended'),
			items: recommendedItems.value,
		},
		{
			key: PbProductType.Package,
			title: t('presentation.packages'),
			items: otherPackages.value,
		},
		{
			key: 'caskets',
			title: t('presentation.caskets'),
			items: caskets.value,
		},
		{
			key: 'urns',
			title: t('presentation.urns'),
			items: urns.value,
		},
		{
			key: PbProductType.Merchandise,
			title: t('presentation.merchandise'),
			items: priceBook.value?.getMiscMerchandise() || [],
		},
		{
			key: PbProductType.CashAdvance,
			title: t('presentation.cashAdvance'),
			items: priceBook.value?.getCashAdvanceProducts() || [],
		},
	]

	// Only include Essentials Packages if the user is authorized to sell Everplans
	if (authStore.isEverplansAuthorized) {
		baseSections.splice(2, 0, {
			key: 'essentialsPackages',
			title: 'Essentials Packages',
			items: essentialsPackages.value,
		})
	}

	return baseSections
})

const displaySections = computed<AccordionSection[]>(() =>
	sections.value.map((section) => ({
		...section,
		items: sortItems(
			section.items.filter((item) =>
				matchesSelectedTag(item, selectedTag.value, { includeUntagged: true })
			)
		),
	}))
)

const handleTagOption = (option: SfTag) => {
	selectedTag.value = option
}

const handleSortByOption = (option: SortByOption) => {
	selectedSortBy.value = option.value
}

watch(displaySections, (nextSections) => {
	const visibleItems = nextSections.flatMap((section) => section.items)
	const selectedVisibleItem = visibleItems.find(
		(item) => item.id === selectedItem.value?.id
	)

	selectedItem.value = selectedVisibleItem ?? visibleItems[0]
})
</script>
